<template>
  <div>
    <CoreContentArea
      class="flex"
    >
      <template v-if="!!data">
        <CoreCard
          icon="envelope"
          title="Contact Us"
          :center="false"
          class="w-full lg:w-1/2"
        >
          <div
            v-if="success"
            class="text-3 absolute"
          >
            <RichTextRenderer
              :document="data.successText"
              :node-renderers="nodeRenderer"
            />
          </div>
          <div
            class="form-fade"
            :class="{ 'opacity-0 pointer-events-none select-none' : success }"
          >
            <div class="text-4 md:text-3">
              <RichTextRenderer
                :document="data.instructionText"
                :node-renderers="nodeRenderer"
              />
            </div>
            <form
              class="mt-4 md:mt-8 flex flex-col"
              novalidate="true"
            >
              <CoreInput
                label="Full Name"
                :required="true"
                :disabled="processing"
                :value.sync="fullName"
                type="text"
                :error="!!containsError('fullName')"
                :error-message="ERROR_CODES[containsError('fullName')]"
                @blur="$event.interacted && isFullname()"
              />
              <CoreInput
                label="Email or Phone Number"
                :required="true"
                :disabled="processing"
                :value.sync="contact"
                type="text"
                :error="!!containsError('contact')"
                :error-message="ERROR_CODES[containsError('contact')]"
                @blur="$event.interacted && isContact()"
              />
              <CoreInput
                label="Subject"
                :disabled="processing"
                :value.sync="subject"
                type="text"
              />
              <CoreInput
                label="Message"
                :disabled="processing"
                :value.sync="message"
                type="text"
                input-type="textarea"
              />
              <CoreAsyncButton
                :suspense="processing"
                @click="handleSubmit"
              />
              <div class="relative">
                <CoreError
                  :visible="attempts > 0 && !success"
                  :message="ERROR_CODES[3]"
                  :position="{
                    transform: 'translateY(-1.75rem)',
                  }"
                />
              </div>
              <p class="text-4">
                This site is protected by reCAPTCHA and the Google
                <a
                  class="underline"
                  href="https://policies.google.com/privacy"
                >Privacy Policy</a> and
                <a
                  class="underline"
                  href="https://policies.google.com/terms"
                >Terms of Service</a> apply.
              </p>
              <Recapture
                ref="recaptcha"
                class="invisible"
              />
            </form>
          </div>
        </CoreCard>
      </template>
    </CoreContentArea>
    <BlockFooter />
  </div>
</template>

<script>

import RichTextRenderer from 'contentful-rich-text-vue-renderer';
import { nodeRenderer } from '@utils';
import { isEmail, isPhone } from '@utils/fieldValidation';
import { ERROR_CODES } from '@const';
import { contact } from '@io';
import { BlockFooter } from '@c/Block';

export default {
  name: 'Contact',

  components: {
    BlockFooter,
    RichTextRenderer,
  },

  data: () => ({
    data: null,
    processing: false,
    fullName: '',
    contact: '',
    subject: '',
    message: '',
    errors: new Map([]),
    errorsChangeTracker: 0,
    nodeRenderer,
    ERROR_CODES,
    success: false,
    attempts: 0,
  }),

  computed: {
    errorsReactive() {
      if (!this.errorsChangeTracker) return [];
      return Array.from(this.errors.entries());
    },
  },

  async created() {
    this.data = await contact.get();
  },

  methods: {
    containsError(fieldName) {
      const error = this.errorsReactive.find((err) => err[0] === fieldName);

      if (typeof error === 'undefined') return false;

      return error[1];
    },
    validateFields() {
      // Clear prev validationErrors
      this.isFullname();

      this.isContact();

      // Return result
      return this.errors.size === 0;
    },
    isFullname() {
      this.errors.delete('fullName');
      // Check Required fields
      if (this.fullName === '') this.errors.set('fullName', 1);

      this.errorsChangeTracker += 1;
    },
    isContact() {
      this.errors.delete('contact');

      if (this.contact === '') {
        this.errors.set('contact', 1);
        this.errorsChangeTracker += 1;
        return;
      }

      // Validate contact field
      // Email
      const isEmailTrue = isEmail(this.contact);
      const isPhoneTrue = isPhone(this.contact);

      if (!(isEmailTrue || isPhoneTrue)) this.errors.set('contact', 2);

      this.errorsChangeTracker += 1;
    },
    async handleSubmit() {
      // Handle all things validaition
      const valid = this.validateFields();
      if (!valid) return;

      // Lets submit
      this.processing = true;

      // Activate recapture
      const recaptcha = await this.$refs.recaptcha.execute();

      // Need to rename the contct assignemt as already declared
      const {
        fullName, contact: _contact, subject, message,
      } = this;

      const result = await contact.submit({
        recaptcha,
        data: {
          fullName, contact: _contact, subject, message,
        },
      });

      this.attempts += 1;

      if (result.data) {
        this.processing = false;
        this.success = true;
      }
    },
  },
};
</script>

<style scoped>
.form-fade {
  transition-property: opacity;
  transition-duration: var(--transition-short);
  transition-timing-function: var(--transition-ease);
}
</style>
